import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, ChevronRight, ChevronLeft, Check } from 'lucide-react';
import { cn } from '../lib/utils';

const OnboardingPage = () => {
  const { completeOnboarding, golfer } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    display_name: golfer?.display_name || '',
    home_city: '',
    home_state: '',
    handicap: '',
    skill_level: 'intermediate',
    preferred_tee: 'middle',
    pace_of_play: 'normal',
    play_style_tags: [],
    bio: '',
    travel_frequency: 'sometimes'
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleTagToggle = (tag) => {
    setFormData(prev => ({
      ...prev,
      play_style_tags: prev.play_style_tags.includes(tag)
        ? prev.play_style_tags.filter(t => t !== tag)
        : [...prev.play_style_tags, tag]
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const submitData = {
        ...formData,
        handicap: formData.handicap ? parseFloat(formData.handicap) : null
      };
      await completeOnboarding(submitData);
      toast.success('Profile complete! Welcome to Fairway Alliance.');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to save profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const skillLevels = [
    { value: 'beginner', label: 'Beginner', desc: 'New to golf or learning' },
    { value: 'intermediate', label: 'Intermediate', desc: 'Comfortable with basics' },
    { value: 'advanced', label: 'Advanced', desc: 'Low handicap, consistent' },
    { value: 'competitive', label: 'Competitive', desc: 'Tournament player' }
  ];

  const teeOptions = [
    { value: 'forward', label: 'Forward Tees' },
    { value: 'middle', label: 'Middle Tees' },
    { value: 'back', label: 'Back Tees' },
    { value: 'tips', label: 'Tips' }
  ];

  const paceOptions = [
    { value: 'fast', label: 'Fast', desc: 'Ready golf, keep moving' },
    { value: 'normal', label: 'Normal', desc: 'Standard pace of play' },
    { value: 'relaxed', label: 'Relaxed', desc: 'Enjoy the round, no rush' }
  ];

  const playStyleTags = [
    'Casual', 'Tournament', 'Networking', 'Travel', 'Lessons', 'Social', 'Early Bird', 'Weekend Warrior'
  ];

  const travelOptions = [
    { value: 'rare', label: 'Rarely', desc: 'Stay local mostly' },
    { value: 'sometimes', label: 'Sometimes', desc: 'Occasional trips' },
    { value: 'often', label: 'Often', desc: 'Frequent traveler' }
  ];

  const states = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'
  ];

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-2xl mx-auto">
        {/* Progress */}
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div 
                className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors",
                  step >= s 
                    ? "bg-gold text-deep-green" 
                    : "bg-white/10 text-white/50"
                )}
              >
                {step > s ? <Check className="w-5 h-5" /> : s}
              </div>
              {s < 3 && (
                <div 
                  className={cn(
                    "w-16 h-1 mx-2",
                    step > s ? "bg-gold" : "bg-white/10"
                  )}
                />
              )}
            </div>
          ))}
        </div>

        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white font-display text-2xl">
              {step === 1 && 'Basic Information'}
              {step === 2 && 'Golf Profile'}
              {step === 3 && 'Preferences'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Step 1: Basic Info */}
            {step === 1 && (
              <>
                <div className="space-y-2">
                  <Label className="text-cream/90">Display Name</Label>
                  <Input
                    name="display_name"
                    value={formData.display_name}
                    onChange={handleChange}
                    placeholder="How you'd like to be known"
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                    data-testid="onboarding-name"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-cream/90">Home City</Label>
                    <Input
                      name="home_city"
                      value={formData.home_city}
                      onChange={handleChange}
                      placeholder="Atlanta"
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                      data-testid="onboarding-city"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-cream/90">State</Label>
                    <select
                      name="home_state"
                      value={formData.home_state}
                      onChange={handleChange}
                      className="w-full h-10 px-4 rounded-sm bg-white/10 border border-white/20 text-white"
                      data-testid="onboarding-state"
                    >
                      <option value="">Select state</option>
                      {states.map((state) => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-cream/90">Bio (Optional)</Label>
                  <Textarea
                    name="bio"
                    value={formData.bio}
                    onChange={handleChange}
                    placeholder="Tell other golfers a bit about yourself..."
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                    rows={3}
                    data-testid="onboarding-bio"
                  />
                </div>
              </>
            )}

            {/* Step 2: Golf Profile */}
            {step === 2 && (
              <>
                <div className="space-y-2">
                  <Label className="text-cream/90">Handicap (Optional)</Label>
                  <Input
                    name="handicap"
                    type="number"
                    step="0.1"
                    value={formData.handicap}
                    onChange={handleChange}
                    placeholder="e.g., 12.5"
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                    data-testid="onboarding-handicap"
                  />
                </div>

                <div className="space-y-3">
                  <Label className="text-cream/90">Skill Level</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {skillLevels.map((level) => (
                      <button
                        key={level.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, skill_level: level.value })}
                        className={cn(
                          "p-4 rounded-sm border text-left transition-colors",
                          formData.skill_level === level.value
                            ? "bg-gold border-gold text-deep-green"
                            : "bg-white/5 border-white/20 text-white hover:border-gold/50"
                        )}
                        data-testid={`skill-${level.value}`}
                      >
                        <p className="font-semibold">{level.label}</p>
                        <p className="text-sm opacity-70">{level.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <Label className="text-cream/90">Preferred Tees</Label>
                  <div className="flex flex-wrap gap-2">
                    {teeOptions.map((tee) => (
                      <button
                        key={tee.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, preferred_tee: tee.value })}
                        className={cn(
                          "px-4 py-2 rounded-sm border transition-colors",
                          formData.preferred_tee === tee.value
                            ? "bg-gold border-gold text-deep-green"
                            : "bg-white/5 border-white/20 text-white hover:border-gold/50"
                        )}
                      >
                        {tee.label}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Step 3: Preferences */}
            {step === 3 && (
              <>
                <div className="space-y-3">
                  <Label className="text-cream/90">Pace of Play</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {paceOptions.map((pace) => (
                      <button
                        key={pace.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, pace_of_play: pace.value })}
                        className={cn(
                          "p-4 rounded-sm border text-center transition-colors",
                          formData.pace_of_play === pace.value
                            ? "bg-gold border-gold text-deep-green"
                            : "bg-white/5 border-white/20 text-white hover:border-gold/50"
                        )}
                      >
                        <p className="font-semibold">{pace.label}</p>
                        <p className="text-xs opacity-70 mt-1">{pace.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <Label className="text-cream/90">Play Style (Select all that apply)</Label>
                  <div className="flex flex-wrap gap-2">
                    {playStyleTags.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => handleTagToggle(tag.toLowerCase())}
                        className={cn(
                          "px-4 py-2 rounded-sm border transition-colors",
                          formData.play_style_tags.includes(tag.toLowerCase())
                            ? "bg-gold border-gold text-deep-green"
                            : "bg-white/5 border-white/20 text-white hover:border-gold/50"
                        )}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <Label className="text-cream/90">How often do you travel for golf?</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {travelOptions.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, travel_frequency: opt.value })}
                        className={cn(
                          "p-4 rounded-sm border text-center transition-colors",
                          formData.travel_frequency === opt.value
                            ? "bg-gold border-gold text-deep-green"
                            : "bg-white/5 border-white/20 text-white hover:border-gold/50"
                        )}
                      >
                        <p className="font-semibold">{opt.label}</p>
                        <p className="text-xs opacity-70 mt-1">{opt.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Navigation */}
            <div className="flex justify-between pt-4">
              {step > 1 ? (
                <Button 
                  variant="outline" 
                  onClick={() => setStep(step - 1)}
                  className="border-white/30 text-white hover:bg-white/10"
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              ) : (
                <div />
              )}
              
              {step < 3 ? (
                <Button 
                  onClick={() => setStep(step + 1)}
                  className="btn-gold"
                  data-testid="onboarding-next"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button 
                  onClick={handleSubmit}
                  className="btn-gold"
                  disabled={loading}
                  data-testid="onboarding-complete"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      Complete Profile
                      <Check className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Skip Option */}
        <p className="text-center mt-6">
          <button 
            onClick={() => navigate('/dashboard')}
            className="text-cream/50 hover:text-cream text-sm"
          >
            Skip for now
          </button>
        </p>
      </div>
    </div>
  );
};

export default OnboardingPage;
